import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher, customFetcherRaw } from '../hooks/customFetcher';
import {
    CalendarEntryMedia,
    downloadMediaZipInputSchema,
    DownloadMediaZipInput,
    deleteMediaInputSchema,
    DeleteMediaInput,
    mediaListSchema,
    mediaUploadResponseSchema,
    renameMediaInputSchema,
    RenameMediaInput,
    mediaDtoSchema,
} from './mediaSchemas';

export const calendarEntryMediaQueryKey = (entryId: number) => ['calendar-entry-media', entryId] as const;

export const useCalendarEntryMedia = (entryId: number) => {
    const { data = [], isLoading, isError, error, refetch } = useQuery<CalendarEntryMedia[], Error>({
        queryKey: calendarEntryMediaQueryKey(entryId),
        queryFn: async () => {
            const response = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${entryId}/media/`,
            });

            return mediaListSchema.parse(response);
        },
        enabled: entryId > 0,
        initialData: [],
        refetchOnWindowFocus: false,
    });

    return {
        media: data,
        isLoadingMedia: isLoading,
        isErrorMedia: isError,
        mediaError: error,
        refetchMedia: refetch,
    };
};

export const useUploadCalendarEntryMedia = (entryId: number) => {
    const queryClient = useQueryClient();

    return useMutation<CalendarEntryMedia[], Error, File[]>({
        mutationFn: async (files) => {
            if (files.length === 0) {
                throw new Error('Bitte mindestens eine Datei auswählen.');
            }

            const formData = new FormData();
            files.forEach((file) => {
                formData.append('files', file);
            });

            const response = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${entryId}/media/`,
                method: 'POST',
                body: formData,
            });

            return mediaUploadResponseSchema.parse(response);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: calendarEntryMediaQueryKey(entryId) });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
        },
    });
};

export const useDeleteMedia = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, DeleteMediaInput>({
        mutationFn: async (input) => {
            const parsedInput = deleteMediaInputSchema.parse(input);

            await customFetcher<void>({
                endpoint: `/media/${parsedInput.mediaId}/`,
                method: 'DELETE',
            });
        },
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({ queryKey: calendarEntryMediaQueryKey(variables.entryId) });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
        },
    });
};

export const useRenameMedia = () => {
    const queryClient = useQueryClient();

    return useMutation<CalendarEntryMedia, Error, RenameMediaInput>({
        mutationFn: async (input) => {
            const parsedInput = renameMediaInputSchema.parse(input);

            const response = await customFetcher<unknown>({
                endpoint: `/media/${parsedInput.mediaId}/`,
                method: 'PATCH',
                body: {
                    title: parsedInput.title,
                },
            });

            return mediaDtoSchema.parse(response);
        },
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({ queryKey: calendarEntryMediaQueryKey(variables.entryId) });
        },
    });
};

export const useDownloadMediaZip = () => useMutation<Blob, Error, DownloadMediaZipInput>({
    mutationFn: async (input) => {
        const parsedInput = downloadMediaZipInputSchema.parse(input);

        const response = await customFetcherRaw({
            endpoint: `/calendar-entries/${parsedInput.entryId}/media/download-zip/`,
            method: 'POST',
            body: {
                media_ids: parsedInput.mediaIds,
            },
        });

        return response.blob();
    },
});
